import { App as CapacitorApp } from '@capacitor/app';
import { CenterPopup, Image, Modal as ModalMobile, Toast } from 'antd-mobile';
import { createContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import termsApi, { TermsNodeListDT } from 'services/apis/cnt2/term-api';
import { useImmer } from 'use-immer';
import { isEmpty } from 'utils/listUtil';
import './App.css';
import styles from './App.module.scss';

import { AppIcon } from '@capacitor-community/app-icon';
import { DeviceSecurityDetect } from '@capacitor-community/device-security-detect';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { PluginListenerHandle } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { PrivacyScreen } from '@capacitor/privacy-screen';
import { StatusBar } from '@capacitor/status-bar';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Button, Modal } from 'antd';
import { ApiCachedSecondsRandom, checkNeedUpdate, getAppVersion, getWebVersion, ThrottleTImeout } from 'app/constant';
import img from 'app/img';
import { compress, decompress } from 'lz-string';
import posthog from 'posthog-js';
import Marquee from 'react-fast-marquee';
import { useDispatch } from 'react-redux';
import UserApi, { UserPolyInfoDT } from 'services/apis/sys/user-api';
import VersionApi, { VersionDataType } from 'services/apis/sys/version-api';
import { setSize } from 'stores/windowSizeSlice';
import { getBrowserFinger } from 'utils/browserFingerUtil';
import { setItemWithExpiration } from 'utils/localStorageUtil';
import { getPlatform, isNative } from 'utils/updateManagerRemote';

// throttleHandler.ts
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { useThrottleFn } from 'ahooks';
import { current } from 'immer';
import { AiFillInfoCircle } from 'react-icons/ai';
import AnnoApi, { AnnoDataType } from 'services/apis/ope/anno-api';

export const useThrottledHandler = (fn: (...args: any[]) => void, wait?: number) => {
  wait = wait || ThrottleTImeout;
  const { run } = useThrottleFn(fn, { wait });
  return run;
};

// 创建 查询client, 持久化client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3, // 重试3次
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: Infinity, // 新鲜缓存2小时 (期间不会重新获取)
      gcTime: Infinity, // 数据保存4小时 (期间保存在内存中) 最大gcTime约为 24 天
      refetchOnWindowFocus: false,
    },
  },
});
const localStoragePersister = createSyncStoragePersister({
  // 使用 localStorage
  storage: window.localStorage,
  key: `TanstackQueryCache`,
  throttleTime: Infinity,
  // 使用lz-string之类的库来压缩和解压缩数据
  serialize: (data) => compress(JSON.stringify(data)),
  deserialize: (data) => JSON.parse(decompress(data)),
});
const sessionQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3, // 重试3次
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 1000 * 3600, // 新鲜缓存1小时 (期间不会重新获取)
      gcTime: 1000 * 3600, // 数据保存1小时 (期间保存在内存中) 最大gcTime约为 24 天
      refetchOnWindowFocus: false,
    },
  },
});
// const sessionStoragePersister = createSyncStoragePersister({
//   // 使用 sessionStorage
//   storage: window.sessionStorage,
//   key: `REACT_QUERY_OFFLINE_CACHE_SESSION`,
//   throttleTime: Infinity,
//   // 使用lz-string之类的库来压缩和解压缩数据
//   serialize: (data) => compress(JSON.stringify(data)),
//   deserialize: (data) => JSON.parse(decompress(data)),
// });

export type NavDT = {
  btmTermList: TermsNodeListDT[];
  top1TermList: TermsNodeListDT[];
  top2TermList: TermsNodeListDT[];
  btmTermNow: TermsNodeListDT;
  top1TermNow: TermsNodeListDT;
  top2TermNow: TermsNodeListDT;
};
export const NavListContext = createContext<{
  navList: NavDT;
  updateNavList: any;
}>(null);

export type TestStateDT = { time: string; speedList: number[] };
export const TestStateContext = createContext<{
  testState: TestStateDT;
  updateTestState: any;
}>(null);

export type NavShowStateDT = {
  showBottomNav: boolean;
  showTop1Nav: boolean;
  showTop2Nav: boolean;
  showTitleNav: boolean;
};
export const NavShowContext = createContext<{
  navShowState: NavShowStateDT;
  updateNavShowState: any;
}>(null);

export const GlobalLoadingContext = createContext<{
  globalLoading: boolean;
  setGlobalLoading: any;
  showGlobalLoading: any;
  closeGlobalLoading: any;
}>(null);

export const ScreenTopContext = createContext({ screenTop: 0 });

export const logDeviceInfo = async () => {
  const info = await Device.getInfo();
  console.log('logDeviceInfo', info);
};

export const logBatteryInfo = async () => {
  const info = await Device.getBatteryInfo();
  console.log('logBatteryInfo', info);
};

// 自托管 更新规则：检测版本是否最新，web使用liveUpdate，app自动下载提示安装
export const checkSelfUpdate = async (showTip?: boolean) => {
  if (getPlatform() != 'native') {
    console.log('非原生环境，无需更新');
    return;
  }
  // console.log('本设备ID:', JSON.stringify(await LiveUpdate.getDeviceId()));
  // console.log('本用户ID', JSON.stringify(await LiveUpdate.getCustomId()));
  // console.log('电池信息:', JSON.stringify(await Device.getBatteryInfo()));
  // console.log('设备信息:', JSON.stringify(await Device.getInfo()));
  // console.log('设备ID:', JSON.stringify(await Device.getId()));
  // console.log('语言标签:', JSON.stringify(await Device.getLanguageTag()));
  // console.log('语言代码:', JSON.stringify(await Device.getLanguageCode()));
  // logDeviceInfo();
  // logBatteryInfo();

  const resp = await VersionApi.pull(null, null);
  if (!resp.successful) {
    console.log('版本更新失败');
    Toast.show('版本更新失败');
    CapacitorApp.exitApp();
    return;
  }

  if (resp.result == null) {
    console.log('没有更新版本');
    return;
  }

  const result: VersionDataType = resp.result;
  const lastVersion = result.versionName;
  if (!(await checkNeedUpdate(lastVersion))) {
    console.log('已是最新版本');
    if (showTip) {
      Toast.show('已是最新版本');
    }
    return;
  }
  console.log(
    '检测到版本更新:',
    result.versionType == 1 ? '内测' : result.versionType == 2 ? '发行' : '修复',
    result.upgradeType == 1 ? '全量' : '在线',
    JSON.stringify(result)
  );

  // 弹窗提示
  Modal.warning({
    centered: true,
    closable: false,
    maskClosable: false,
    keyboard: false,
    width: window.innerWidth * 0.75,
    title: <div className=" my-1 text-base">检测到新版本 {lastVersion}</div>,
    content: (
      <>
        {/* <div className="text-pink-500 font-bold">
          <div>更新内容：</div>
          <div>{result.versionDesc || '无'}</div>
        </div> */}
        <div className="font-bold">
          <div>更新内容：{result.versionDetail || '暂无描述'}</div>
        </div>
        <div className="font-bold">
          温馨提示：
          <span className="text-pink-500">
            {result.upgradeType == 1
              ? '此次为【版本更新】，请重新下载并安装APP'
              : '此次为【内容更新】，点击更新后请勿离开'}
          </span>
        </div>
      </>
    ),
    onOk: () => {
      // 退出APP
      // CapacitorApp?.exitApp();
      // 返回一个永远不 resolve 的 Promise
      return new Promise(() => {});
    },
    onCancel: () => {
      CapacitorApp?.exitApp();
    },
    onClose: () => {
      CapacitorApp?.exitApp();
    },
    okText: (
      <div
        onClick={async () => {
          if (result.upgradeType == 1) {
            // 全量更新
            window.open('https://fabu.uutt.me/', '_blank');
          } else {
            // 在线更新
            try {
              console.log('待下载');
              // 尝试恢复
              const bundleIds = (await LiveUpdate.getBundles()).bundleIds;
              if (bundleIds.includes(lastVersion)) {
                console.log('已存在版本缓存');
              } else {
                await LiveUpdate.downloadBundle({
                  url: result.fileLink,
                  bundleId: lastVersion,
                });
                console.log('已下载');
              }
              await LiveUpdate.setNextBundle({ bundleId: lastVersion });
              console.log('已设置');
              await LiveUpdate.ready();
              console.log('已就绪');
              VersionApi.overLoad(result.versionKey);
              Modal.warning({
                centered: true,
                closable: false,
                maskClosable: false,
                keyboard: false,
                width: window.innerWidth * 0.75,
                title: '更新资源',
                okText: '确认',
                onOk: async () => {
                  console.log('getCurrentBundle_2', JSON.stringify(await LiveUpdate.getCurrentBundle()));
                  await LiveUpdate.reload();
                },
              });
            } catch (e) {
              Toast.show('更新异常');
              console.log(e);
            }
          }
        }}
      >
        点击更新
      </div>
    ),
    style: {
      top: 200,
    },
  });
};

// appflow 更新规则：手动检测appVersion，webVersion会自动检测
export const checkAppVersion = async (showTip?: boolean) => {
  const resp = await VersionApi.pull(null, null);
  if (resp.successful) {
    const result: VersionDataType = resp.result;
    console.log('本地版号:', getAppVersion(), getWebVersion());
    console.log('远程版号:', getAppVersion(result.versionName), getWebVersion(result.versionName));
    if (getAppVersion() >= getAppVersion(result.versionName)) {
      console.log('已是最新版本');
      if (showTip) {
        Toast.show('已是最新版本');
      }
    } else {
      console.log('检测到版本更新');
      Modal.warning({
        title: '检测到版本更新',
        centered: true,
        closable: false,
        maskClosable: false,
        keyboard: false,
        width: window.innerWidth * 0.75,
        onOk: () => {
          // 退出APP
          CapacitorApp?.exitApp();
          // 返回一个永远不 resolve 的 Promise
          return new Promise(() => {});
        },
        onCancel: () => {
          CapacitorApp?.exitApp();
        },
        onClose: () => {
          CapacitorApp?.exitApp();
        },
        okText: (
          <div
            onClick={() => {
              window.open('https://fabu.uutt.me/', '_blank');
              CapacitorApp?.exitApp();
            }}
          >
            去更新
          </div>
        ),
        style: {
          top: 200,
        },
      });
    }
  } else {
    console.log('版本检测失败');
  }
};

export const fetchAnno = async (annoId: string) => {
  const resp = await AnnoApi.get(annoId);
  const anno: AnnoDataType = resp.result;
  if (resp.successful && anno) {
    ModalMobile.show({
      title: anno.annoName,
      showCloseButton: true,
      closeOnAction: true,
      closeOnMaskClick: true,
      content: (
        <div
          className="text-sm text-nowrap whitespace-nowrap"
          dangerouslySetInnerHTML={{
            __html: anno.annoContent,
          }}
        />
      ),
    });
  }
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showTopNarrow, setShowTopNarrow] = useState<boolean>(false);
  // const [showBackBar, setShowBackBar] = useState<boolean>(false);
  const hiddenTopNarrowList = [
    '/',
    '/sesame',
    '/player',
    '/reader-picture',
    '/reader-image',
    '/reader-manga',
    '/one/mine',
  ];
  // const showBackBarList = ["search", "term"];
  // 监控路由
  useEffect(() => {
    let showTopNarrow = true;
    let showBackBar = false;
    // 包含这些路径，则不显示
    hiddenTopNarrowList.forEach((page) => {
      if (location.pathname === page) {
        showTopNarrow = false;
      }
    });
    setShowTopNarrow(showTopNarrow);
    // 滚动高度
    // if (showTopNarrow && window.pageYOffset > 100) {
    //   setShowTopNarrow(true);
    // } else {
    //   setShowTopNarrow(false);
    // }
    console.log('pathname', location.pathname, showTopNarrow, window.pageYOffset);

    // 包含这些路径，则不显示
    // showBackBarList.forEach((page) => {
    //   if (location.pathname.indexOf(page) != -1) {
    //     showBackBar = true;
    //   }
    // });
    // setShowBackBar(showBackBar);
  }, [location.pathname, window.scrollY, window.pageYOffset]);

  const [loading, toggleLoading] = useState(true);
  const [globalLoading, setGlobalLoading] = useState(false);
  const showGlobalLoading = (data?: { timeout?: number; long?: boolean }) => {
    if (!globalLoading) {
      setGlobalLoading(true);
      setTimeout(
        () => {
          setGlobalLoading(false);
        },
        // timeout(自定义) -> long(3000) -> short(1000)
        data?.timeout || (data?.long ? 3 * 1000 : 1.0 * 1000) // 只控制显示时长，不会阻塞数据请求
      );
    }
  };
  const closeGlobalLoading = () => {
    setGlobalLoading(false);
  };
  // 使用 React Portal 渲染 CenterPopup 到 body
  const renderCenterPopup = () => {
    if (globalLoading) {
      return ReactDOM.createPortal(
        <CenterPopup
          className={styles.mystyle}
          visible={globalLoading}
          closeOnMaskClick={false}
          showCloseButton={false}
          onClose={() => {
            setGlobalLoading(false);
          }}
        >
          <div className=" flex flex-col justify-center items-center my-5 gap-5">
            <img
              src={img.touhouLoading}
              width={window.innerWidth / 6}
              height={window.innerWidth / 6}
            />
          </div>
        </CenterPopup>,
        document.body // 将 CenterPopup 渲染到 body 元素中
      );
    }
    return null;
  };

  const [navList, updateNavList] = useImmer<NavDT>({
    btmTermList: [],
    top1TermList: [],
    top2TermList: [],
    btmTermNow: null,
    top1TermNow: null,
    top2TermNow: null,
  });
  const [testState, updateTestState] = useImmer<TestStateDT>({
    time: new Date().toLocaleString(),
    speedList: new Array(9).fill(0).map((item, index) => Math.floor(Math.random() * 400 + 100)),
  });
  const [navShowState, updateNavShowState] = useImmer({
    showBottomNav: true,
    showTop1Nav: true,
    showTop2Nav: true,
    showTitleNav: false,
  });

  const fetchTreeTerm = async (id?: any, initial = true) => {
    const response = await termsApi.treeTerm({}, {});
    if (response.code === 0 && response.successful) {
      const result = response.result as TermsNodeListDT;
      // CAT_1 - CAT_1_2 - CAT_1_2_2
      // 底部导航
      const _btmTermNow = result.termsNodeList[0];
      // 一级导航
      const _top1NavList = isEmpty(_btmTermNow.termsNodeList) ? [] : _btmTermNow.termsNodeList;
      // 二级导航 (默认 动画-里番)
      const _top2NavList = isEmpty(_top1NavList)
        ? []
        : _top1NavList.filter((item) => item.termUuid === 'CAT_1_1')[0].termsNodeList;

      let _navList;
      if (initial) {
        // 更新导航
        updateNavList((draft) => {
          draft.btmTermList = result.termsNodeList;
          draft.top1TermList = _top1NavList;
          draft.top2TermList = _top2NavList;
          draft.btmTermNow = _btmTermNow;
          draft.top1TermNow = _top1NavList.filter((item) => item.termUuid === 'CAT_1_1')[0];
          draft.top2TermNow = _top2NavList.filter((item) => item.termUuid === 'CAT_1_1_2')[0];
          _navList = { ...draft };
        });
      }
      // console.log("_navList", _navList);
      return _navList;
    } else {
      Toast.show(response.msg);
    }
  };

  // 监控导航列表
  useEffect(() => {
    // console.log("App-navList", navList);
  }, [navList]);

  // 拦截 F5 和 Ctrl+R 键
  useEffect(() => {
    // 禁止快捷键刷新
    const handleKeyDown = (event) => {
      if (event.key === 'F5' || (event.ctrlKey && event.key === 'r') || (event.metaKey && event.key === 'r')) {
        event.preventDefault();
        // 在这里添加你想要执行的操作，例如刷新数据而不刷新页面
        return false;
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    // const handleBeforeUnload = (event) => {
    //   event.preventDefault();
    //   // 标准的做法：设置 event.returnValue
    //   event.returnValue = "确认关闭?";
    //   return "确认关闭?";
    // };
    // window.addEventListener("beforeunload", handleBeforeUnload);

    // 禁止右键
    document.oncontextmenu = () => false;

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // 请求分类数据
  useEffect(() => {
    fetchTreeTerm();
  }, []);

  const InitialTermDataTodo = () => {
    const treeTremQuery = useQuery({
      queryKey: ['treeTerm'],
      queryFn: () => {
        return fetchTreeTerm();
      },
      enabled: !loading,
    });

    return (
      <div className=" hidden">
        <div>
          {treeTremQuery.isFetching && <>获取中...</>}
          {treeTremQuery.isError && <>获取失败: {treeTremQuery.error.message}</>}
          {treeTremQuery.isSuccess && <>获取成功: {JSON.stringify(treeTremQuery.data, null, 2)}</>}
        </div>
      </div>
    );
  };

  const getBaseInfoByFinger = () => {
    getBrowserFinger().then((data) => {
      // console.log("getBrowserFinger", data);
      // 请求用户
      UserApi.getByBrowserFinger(data).then((userResult) => {
        if (userResult.successful) {
          // console.log("getByBrowserFinger", userResult.result);
          const _userPolyInfo = userResult.result as UserPolyInfoDT;
          // 设置登录用户
          setItemWithExpiration('userPolyInfo', _userPolyInfo, ApiCachedSecondsRandom);
          // console.log('userPolyInfo', getItemWithExpiration('userPolyInfo'));
          // // 请求用户详情
          // fetchUser(userResult.result.uuid);

          // 不是会员, 跳转异次元宝盒 -> 都跳转异次元宝盒

          toggleLoading(false);
          navigate('/sesame');

          // if (!_userPolyInfo.asUserDetail.fkUserGroupUuid) {
          //   setTimeout(() => {
          //     navigate('/sesame');
          //   }, 1000);
          // } else {
          //   navigate('/one/home');
          // }
        } else {
          navigate('/login');
          Toast.show(userResult.msg);
        }
      });
    });
  };

  // 查询用户和初始化
  // const fetchUser = async (userId: string) => {
  //   // 获取用户详细信息
  //   const userDetailResult = await UserDetailApi.getByUserId(userId);
  //   if (userDetailResult.code === 0 && userDetailResult.successful) {
  //     const userGroupId = userDetailResult.result?.fkUserGroupUuid;
  //     // 设置会员信息
  //     setItemWithExpiration("vip", { userGroupId: userGroupId }, ApiCachedSecondsRandom);
  //     // 获取用户的视频列表(收藏列表和观看历史)
  //     const result2 = await VideoListApi.getVideoListId(userId);
  //     if (result2.code === 0 && result2.successful) {
  //       setItemWithExpiration("listId", result2.result, ApiCachedSecondsRandom);
  //     }
  //     // 如果是普通会员, 跳转到芝麻开门页面
  //     console.log("userDetailResult", userDetailResult.result);
  //     // 不是会员, 加入异次元宝盒
  //     if (!userGroupId) {
  //       navigate("/sesame");
  //       toggleLoading(false);
  //     }
  //     // 否则手动点击进入
  //   }
  // };
  // 请求用户数据
  useEffect(() => {
    // 监测更新
    checkSelfUpdate();
    // 检查App版本更新（AppVersion）
    // checkAppUpdate();
    // 检查Web版本更新（WebVersion）
    // setTimeout(checkWebUpdate, 5000);
    // checkForUpdates(); // 废弃不使用

    // 根据浏览器指纹获取基础信息
    getBaseInfoByFinger();
    // 弹窗公告
    setTimeout(() => {
      fetchAnno('guestAnno');
    }, 3000);
  }, []);

  const dispatch = useDispatch();

  // 监控页面宽高
  useEffect(() => {
    dispatch(setSize({ width: window.innerWidth, height: window.innerHeight }));
    // console.log("browserInfo", window.innerWidth, window.innerHeight);
  }, []);

  // 设置返回按钮监听
  const [exitModalVisible, changeExitModalVisible] = useState(false);

  // OK
  // CapacitorApp.addListener('appStateChange', ({ isActive }) => {
  //   console.log('CapacitorApp state changed. Is active?', isActive);
  // });
  // CapacitorApp.addListener('appUrlOpen', (data) => {
  //   console.log('CapacitorApp opened with URL:', data);
  // });
  // CapacitorApp.addListener('appRestoredResult', (data) => {
  //   console.log('CapacitorApp Restored state:', data);
  // });
  // // OK
  // CapacitorApp.addListener('pause', () => {
  //   console.log('CapacitorApp state pause.');
  // });
  // // OK
  // CapacitorApp.addListener('resume', () => {
  //   console.log('CapacitorApp opened resume');
  // });
  // // OK
  // CapacitorApp.addListener('backButton', ({ canGoBack }) => {
  //   console.log('CapacitorApp active backButton:' + canGoBack);
  // });
  useEffect(() => {
    let backButtonListener;
    const setupListener = async () => {
      backButtonListener = await CapacitorApp.addListener('backButton', () => {
        // console.log('backButton-history', JSON.stringify(window.history), window.history, window.history.length);
        // console.log('backButton-location', JSON.stringify(window.location));
        if (
          window.location.pathname.endsWith('/') ||
          window.location.pathname.endsWith('/sesame') ||
          window.location.pathname.endsWith('/one/osusume')
        ) {
          // 显示退出确认弹窗
          changeExitModalVisible(true);
          return;
        }
        window.history.back();
      });
    };
    setupListener();
    return () => {
      backButtonListener?.remove();
    };
  }, []);

  // 监听窗口高度变化，记录初始的 window.innerHeight，在输入法弹出时动态调整页面布局。
  let initialHeight = window.innerHeight;
  useEffect(() => {
    //@ts-ignore
    if (document.getElementsByClassName('App')[0]?.style) {
      //@ts-ignore
      document.getElementsByClassName('App')[0].style.height = '100px';
    }

    document.body.style.height = '100px';
    const handleResize = () => {
      Toast.show(document.body.style.height);
      const currentHeight = window.innerHeight;
      const isKeyboardVisible = currentHeight < initialHeight;

      if (isKeyboardVisible) {
        // 输入法弹出时
        // document.body.style.height = `${currentHeight}px`;
        // document.body.style.overflow = 'hidden'; // 防止页面滚动
        // 输入法弹出时，使用 transform 平移内容避免样式无效
        // document.body.style.transform = `translateY(-${initialHeight - currentHeight}px)`;
        // document.body.style.transition = 'transform 0.2s ease-out'; // 平滑动画
        // 输入法弹出时，设置固定定位
        document.body.style.position = 'fixed';
        document.body.style.top = `-${initialHeight - currentHeight}px`;
        document.body.style.width = '100%';
        document.body.style.height = `${initialHeight}px`;
        document.body.style.overflow = 'hidden';
        Toast.show('弹起输入法');
      } else {
        // 输入法收起时
        // document.body.style.height = '100vh';
        // document.body.style.overflow = 'auto';
        // document.body.style.transform = 'translateY(0)';
        // 输入法收起时，恢复正常
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.height = '';
        document.body.style.overflow = 'auto';
        Toast.show('收起输入法');
      }
    };
    // window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      // document.body.style.height = '100vh';
      // document.body.style.overflow = 'auto'; // 恢复滚动
      // document.body.style.transform = 'translateY(0)';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.height = '';
      document.body.style.overflow = 'auto';
    };
  }, []);

  const keepAwake = async () => {
    await KeepAwake.keepAwake();
    console.log('isKeptAwake:', JSON.stringify(await KeepAwake.isKeptAwake()));
  };
  const enablePrivacy = async () => {
    await PrivacyScreen.enable({
      android: { dimBackground: true, preventScreenshots: true },
      ios: {
        blurEffect: 'dark',
      },
    });
    console.log('isEnabledPrivacy:', JSON.stringify(await PrivacyScreen.isEnabled()));
  };
  const isJailBreakOrRooted = async () => {
    const { value } = await DeviceSecurityDetect.isJailBreakOrRooted();
    console.log('isJailBreakOrRooted', value);
  };
  const hideStatusBar = async () => {
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.hide();
    console.log('hideStatusBar', JSON.stringify(await StatusBar.getInfo()));
  };
  const lockPortrait = async () => {
    await ScreenOrientation.lock({
      orientation: 'portrait',
    });
    console.log('lockPortrait', JSON.stringify(await ScreenOrientation.orientation()));
  };
  const getNetworkStatus = async () => {
    const status = await Network.getStatus();
    console.log('getNetworkStatus', JSON.stringify(status));
    updateNetwork((draft) => {
      draft.showModal = status.connectionType == 'none';
      draft.connectionType = status.connectionType;
    });
  };

  const [network, updateNetwork] = useImmer({ showModal: false, connectionType: 'unknown' });

  // 保持唤醒,隐私屏,全屏模式,锁定竖屏,检测越狱/ROOT,网络状态
  useEffect(() => {
    const platform = getPlatform();
    if (platform != 'native') {
      console.log('当前平台为' + platform + '，不支持插件功能');
      return;
    }
    console.log('当前平台为' + platform + '，可支持插件功能');

    keepAwake(); // 保持唤醒
    enablePrivacy(); // 开启隐私屏（仅适用于 Android 和 iOS）
    hideStatusBar(); // 全屏模式（隐藏状态栏）
    lockPortrait(); // 锁定竖屏
    isJailBreakOrRooted(); // 检测越狱/ROOT
    getNetworkStatus(); // 网络状态

    // 监听网络状态（里面无法获取到最新的status。。）
    Network.addListener('networkStatusChange', (status) => {
      updateNetwork((draft) => {
        draft.showModal = status.connectionType != draft.connectionType;
        draft.connectionType = status.connectionType;
      });
    });
    return () => {
      Network.removeAllListeners();
    };
  }, []);

  // 全局配置Toast提示
  Toast.config({ position: 'bottom', duration: 3000, maskClickable: true });

  // 页面内容
  return (
    <QueryClientProvider client={queryClient}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister: localStoragePersister }}
      >
        {/* <QueryClientProvider client={sessionQueryClient}>
          <PersistQueryClientProvider
            client={sessionQueryClient}
            persistOptions={{ persister: sessionStoragePersister }}
          > */}

        <Modal
          centered={true}
          closable={false}
          maskClosable={false}
          open={network.showModal}
          width={window.innerWidth * 0.75}
          title={
            <div className=" flex justify-start items-center gap-2">
              <AiFillInfoCircle
                size={18}
                color="orange"
              />
              <div className=" text-sm">{network.connectionType == 'none' ? '网络连接失败' : '网络连接恢复'}</div>
            </div>
          }
          footer={
            <Button
              type="primary"
              onClick={() => {
                if (network.connectionType == 'none') {
                  showGlobalLoading();
                } else {
                  updateNetwork((draft) => {
                    draft.showModal = false;
                  });
                }
              }}
            >
              {network.connectionType == 'none' ? '再次尝试' : '我知道了'}
            </Button>
          }
        />

        {/* ReactQuery调试 */}
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition={'top-right'}
        />

        {/* 不实用。。已隐藏 */}
        {/* 返回顶部浮动按钮 */}
        {/* {showTopNarrow && (
          <>
            <FloatingBubble
              axis="y" // 移动
              magnetic="y" // 吸附
              style={{
                '--initial-position-bottom': '60px',
                '--initial-position-right': '24px',
                '--edge-distance': '24px',
                '--background': '#FB923C',
              }}
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <AiOutlineArrowUp fontSize={32} />
            </FloatingBubble>
          </>
        )} */}

        {/* 渲染 CenterPopup 到 body 中 */}
        {renderCenterPopup()}

        {/* 全局加载蒙版 */}
        {/* 使用 React Portal 时，组件会被渲染到 DOM 中指定的位置，而不是它的父级容器。这使得我们可以将 CenterPopup 渲染到 body 中，避免它与其他组件（如 Modal）共享同一个容器。 */}
        {/* {true && (
          <>
            {ReactDOM.createPortal(
              <CenterPopup
                className={styles.mystyle}
                visible={globalLoading}
                closeOnMaskClick={false}
                showCloseButton={false}
                onClose={() => {
                  setGlobalLoading(false);
                }}
              >
                <div className=" flex flex-col justify-center items-center my-5 gap-5">
                  <img
                    src={img.touhouLoading}
                    width={window.innerWidth / 6}
                    height={window.innerWidth / 6}
                  />
                </div>
              </CenterPopup>,
              document.body // 将 CenterPopup 渲染到 body 元素中
            )}
          </>
        )} */}

        {/* 少女加载中 */}
        {loading && (
          <>
            <div
              style={{
                width: window.innerWidth,
                height: window.innerHeight,
                fontFamily: 'Shuhei',
              }}
              className=" bg-black text-white flex flex-col justify-center items-center gap-3"
            >
              <div className=" fixed left-0 bottom-0 text-center w-full text-base p-10">异次元宝盒</div>
              <div className=" rounded-[200px] border-2 border-white p-14 text-center relative overflow-hidden">
                <Image
                  src={img.touhouLoading}
                  width={100}
                  height={100}
                  placeholder={<div style={{ width: 100, height: 100 }}></div>}
                />
                <div
                  style={{
                    textAlign: 'center',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translate(-50%,-100%)',
                  }}
                >
                  <Marquee
                    speed={20}
                    pauseOnHover={true}
                    pauseOnClick={true}
                    autoFill={true}
                  >
                    <div className=" pl-10">少女祈祷中</div>
                  </Marquee>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 渲染子路由 */}
        {!loading && (
          <>
            <InitialTermDataTodo />
            <TestStateContext.Provider value={{ testState: testState, updateTestState: updateTestState }}>
              <NavListContext.Provider value={{ navList: navList, updateNavList: updateNavList }}>
                <NavShowContext.Provider
                  value={{
                    navShowState: navShowState,
                    updateNavShowState: updateNavShowState,
                  }}
                >
                  <ScreenTopContext.Provider value={{ screenTop: window.screenTop }}>
                    <GlobalLoadingContext.Provider
                      value={{
                        globalLoading: globalLoading,
                        setGlobalLoading: setGlobalLoading,
                        showGlobalLoading: showGlobalLoading,
                        closeGlobalLoading: closeGlobalLoading,
                      }}
                    >
                      <div className="App">
                        <div
                          className="bg-gradient-to-br from-purple-200 to-orange-200/80"
                          style={{ minHeight: window.innerHeight }}
                        >
                          <Outlet />
                        </div>
                      </div>
                    </GlobalLoadingContext.Provider>
                  </ScreenTopContext.Provider>
                </NavShowContext.Provider>
              </NavListContext.Provider>
            </TestStateContext.Provider>
          </>
        )}

        {/* 弹窗 */}
        <Modal
          title="提示"
          centered={true}
          closable={false}
          maskClosable={true}
          open={exitModalVisible}
          onOk={() => {
            if (isNative()) {
              setTimeout(() => {
                CapacitorApp.exitApp();
              }, 1500);
            }
          }}
          onCancel={() => {
            changeExitModalVisible(false);
          }}
          okText="确定"
          cancelText="取消"
          width={window.innerWidth * 0.75}
        >
          <p>确定退出异次元空间?</p>
        </Modal>

        {/* </PersistQueryClientProvider>
        </QueryClientProvider> */}
      </PersistQueryClientProvider>
    </QueryClientProvider>
  );
}

export default App;
